<script lang="ts">
  import { SERVER } from '../lib/stores';

  interface MediaItem {
    url: string;
    sha256: string;
    size: number;
    type: string;
    owner: string;
    uploaded: number;
    expires_at: number;
    downloads: number;
    paid: boolean;
    thumb_url: string | null;
    preview_url: string;
  }

  let items = $state<MediaItem[]>([]);
  let loading = $state(true);
  let loadingMore = $state(false);
  let hasMore = $state(false);
  let sort = $state('recent');
  let selected = $state<MediaItem | null>(null);
  let throttleTier = $state('');
  let throttleSpeed = $state(0);
  let daysLeft = $state(0);
  let speedupStatus = $state('');
  let paidUrl = $state<string | null>(null);
  let typeFilter = $state('');

  $effect(() => {
    loadGallery();
  });

  $effect(() => {
    if (selected) {
      paidUrl = null;
      speedupStatus = '';
      checkThrottle(selected);
    }
  });

  async function checkThrottle(item: MediaItem) {
    try {
      const resp = await fetch(`${SERVER}/${item.sha256}`, { method: 'HEAD' });
      throttleTier = resp.headers.get('x-download-tier') || '';
      throttleSpeed = parseInt(resp.headers.get('x-throttle-speed') || '0', 10);
      daysLeft = parseInt(resp.headers.get('x-days-until-expiry') || '0', 10);
    } catch {
      throttleTier = '';
    }
  }

  async function payForSpeed(item: MediaItem) {
    speedupStatus = 'Minting 1 sat...';
    try {
      const { payForUpload } = await import('../lib/cashu-payment');
      const token = await payForUpload(2, (s: string) => { speedupStatus = s; });
      speedupStatus = 'Fetching at full speed...';

      const resp = await fetch(`${SERVER}/${item.sha256}`, {
        headers: { 'X-Cashu': token },
      });
      const blob = await resp.blob();
      paidUrl = URL.createObjectURL(blob);
      throttleTier = resp.headers.get('x-download-tier') || 'paid';
      speedupStatus = '';
    } catch {
      speedupStatus = 'Payment failed — serving at normal speed';
      setTimeout(() => { speedupStatus = ''; }, 3000);
    }
  }

  async function loadGallery() {
    loading = true;
    try {
      const params = new URLSearchParams({ sort, limit: '60' });
      if (typeFilter) params.set('type', typeFilter);
      const resp = await fetch(`${SERVER}/explore?${params}`);
      if (resp.ok) {
        const data = await resp.json();
        items = data.items;
        hasMore = data.count >= 60;
      }
    } catch {
      // network error
    }
    loading = false;
  }

  async function loadMore() {
    if (loadingMore || !hasMore) return;
    loadingMore = true;
    try {
      const params = new URLSearchParams({ sort, limit: '60', offset: String(items.length) });
      if (typeFilter) params.set('type', typeFilter);
      const resp = await fetch(`${SERVER}/explore?${params}`);
      if (resp.ok) {
        const data = await resp.json();
        items = [...items, ...data.items];
        hasMore = data.count >= 60;
      }
    } catch {
      // network error
    }
    loadingMore = false;
  }

  function formatSize(bytes: number): string {
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(2) + ' GB';
    if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return bytes + ' B';
  }

  function timeAgo(ts: number): string {
    const diff = Date.now() / 1000 - ts;
    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    return Math.floor(diff / 86400) + 'd ago';
  }

  function isImage(type: string) { return type.startsWith('image/'); }
  function isVideo(type: string) { return type.startsWith('video/'); }

  function openViewer(item: MediaItem) {
    selected = item;
  }

  function closeViewer() {
    selected = null;
  }
</script>

<svelte:window onkeydown={(e) => e.key === 'Escape' && closeViewer()} />

<div class="space-y-4">
  <div class="flex items-center justify-between flex-wrap gap-2">
    <h2 class="text-xl font-bold text-[#e6edf3]">Explore</h2>
    <div class="flex gap-2 flex-wrap">
      <div class="flex gap-1">
        <button class="px-2 py-1 text-[10px] rounded-md transition-colors {typeFilter === '' ? 'bg-[#58a6ff] text-white' : 'bg-[#21262d] text-[#8b949e] hover:text-[#e6edf3]'}" onclick={() => { typeFilter = ''; }}>All</button>
        <button class="px-2 py-1 text-[10px] rounded-md transition-colors {typeFilter === 'image' ? 'bg-[#58a6ff] text-white' : 'bg-[#21262d] text-[#8b949e] hover:text-[#e6edf3]'}" onclick={() => { typeFilter = 'image'; }}>🖼️ Images</button>
        <button class="px-2 py-1 text-[10px] rounded-md transition-colors {typeFilter === 'video' ? 'bg-[#58a6ff] text-white' : 'bg-[#21262d] text-[#8b949e] hover:text-[#e6edf3]'}" onclick={() => { typeFilter = 'video'; }}>🎬 Videos</button>
      </div>
      <div class="flex gap-1">
        {#each ['recent', 'popular', 'large'] as option}
          <button
            class="px-2 py-1 text-[10px] rounded-md transition-colors {sort === option ? 'bg-[#238636] text-white' : 'bg-[#21262d] text-[#8b949e] hover:text-[#e6edf3]'}"
            onclick={() => { sort = option; }}
          >
            {option === 'recent' ? '🕐 Recent' : option === 'popular' ? '🔥 Popular' : '📦 Largest'}
          </button>
        {/each}
      </div>
    </div>
  </div>

  {#if loading}
    <div class="flex items-center justify-center py-16">
      <div class="w-8 h-8 rounded-full border-2 border-[#21262d] border-t-[#58a6ff] animate-spin"></div>
    </div>
  {:else if items.length === 0}
    <div class="text-center py-20 px-4">
      <svg class="w-16 h-16 mx-auto text-[#21262d] mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
      </svg>
      <p class="text-[#8b949e] text-sm mb-2">No media files yet</p>
      <p class="text-[#484f58] text-xs">Upload an image or video to see it featured here.</p>
      <a href="#/upload" class="inline-block mt-4 px-4 py-2 bg-[#238636] hover:bg-[#2ea043] text-white rounded-md text-xs font-medium transition-colors">
        Upload Media
      </a>
    </div>
  {:else}
    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
      {#each items as item (item.sha256)}
        <a
          href="#/media/{item.sha256}"
          class="group relative aspect-square bg-[#161b22] border border-[#21262d] rounded-lg overflow-hidden hover:border-[#30363d] transition-all block"
        >
          {#if isImage(item.type)}
            <img
              src={item.preview_url}
              alt={item.sha256.slice(0, 12)}
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              loading="lazy"
            />
          {:else if isVideo(item.type)}
            <div class="w-full h-full flex items-center justify-center bg-[#0d1117]">
              <svg class="w-10 h-10 text-[#484f58] group-hover:text-[#58a6ff] transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.328l5.603 3.113z" />
              </svg>
            </div>
            <div class="absolute top-2 left-2 px-1.5 py-0.5 bg-black/70 rounded text-[10px] text-white font-medium">
              VIDEO
            </div>
          {/if}

          {#if item.paid}
            <div class="absolute top-2 right-2 px-1.5 py-0.5 bg-[#238636]/90 rounded text-[10px] text-white font-medium">
              ⚡
            </div>
          {/if}

          <div class="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
            <div class="flex items-center justify-between">
              <span class="text-[10px] text-[#8b949e]">{formatSize(item.size)}</span>
              <span class="text-[10px] text-[#484f58]">↓ {item.downloads}</span>
            </div>
          </div>
        </a>
      {/each}
    </div>
    {#if hasMore}
      <div class="flex justify-center pt-4">
        <button
          class="px-4 py-2 bg-[#21262d] hover:bg-[#30363d] text-[#e6edf3] rounded-md text-xs font-medium transition-colors"
          onclick={loadMore}
          disabled={loadingMore}
        >
          {loadingMore ? 'Loading...' : 'Load More'}
        </button>
      </div>
    {/if}
  {/if}
</div>

{#if selected}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
    onclick={closeViewer}
    role="button"
    tabindex="0"
    onkeydown={(e) => e.key === 'Enter' && closeViewer()}
  >
    <div class="bg-[#161b22] border border-[#21262d] rounded-lg max-w-4xl max-h-[90vh] w-full overflow-hidden flex flex-col" onclick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" tabindex="-1">
      <div class="flex items-center justify-between px-4 py-3 border-b border-[#21262d]">
        <div class="flex items-center gap-2 min-w-0">
          <span class="text-sm text-[#e6edf3] font-medium">
            {isImage(selected.type) ? '🖼️' : '🎬'} {selected.type}
          </span>
          <span class="text-xs text-[#484f58]">{formatSize(selected.size)}</span>
          {#if selected.downloads > 0}
            <span class="text-xs text-[#484f58]">↓ {selected.downloads}</span>
          {/if}
          {#if throttleTier === 'throttled' || throttleTier === 'critical'}
            <span class="text-[10px] px-1.5 py-0.5 rounded bg-[#d299221a] text-[#d29922]">{throttleSpeed} MB/s</span>
          {:else if throttleTier === 'premium' || throttleTier === 'paid'}
            <span class="text-[10px] px-1.5 py-0.5 rounded bg-[#2386361a] text-[#3fb950]">⚡ Full speed</span>
          {/if}
        </div>
        <button onclick={closeViewer} class="text-[#8b949e] hover:text-[#e6edf3] p-1">
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="flex-1 overflow-auto flex items-center justify-center bg-[#0d1117] min-h-[300px]">
        {#if isImage(selected.type)}
          <img src={paidUrl || selected.preview_url} alt={selected.sha256} class="max-w-full max-h-[70vh] object-contain" />
        {:else if isVideo(selected.type)}
          <video src={paidUrl || selected.preview_url} controls class="max-w-full max-h-[70vh]" />
        {/if}
      </div>

      <div class="px-4 py-3 border-t border-[#21262d] bg-[#0d1117]/50 space-y-2">
        {#if (throttleTier === 'throttled' || throttleTier === 'critical') && !paidUrl}
          <div class="flex items-center gap-2 px-3 py-2 rounded-md bg-[#1c1525] border border-[#341f6e]">
            <span class="text-xs text-[#a371f7] flex-1">
              {speedupStatus || `Speed: ${throttleSpeed} MB/s (${daysLeft} days until expiry)`}
            </span>
            {#if !speedupStatus}
              <button
                class="shrink-0 px-3 py-1.5 text-xs font-medium rounded-md bg-[#238636] hover:bg-[#2ea043] text-white transition-colors"
                onclick={() => payForSpeed(selected)}
              >
                ⚡ Pay 1 sat for full speed
              </button>
            {/if}
          </div>
        {/if}
        <div class="flex items-center gap-2">
          <code class="flex-1 text-xs font-mono text-[#58a6ff] bg-[#0d1117] px-3 py-2 rounded-md border border-[#21262d] break-all select-all">
            {selected.url}
          </code>
          <button
            class="shrink-0 px-3 py-2 text-xs font-medium rounded-md border border-[#21262d] bg-[#21262d] hover:bg-[#30363d] text-[#e6edf3] transition-colors"
            onclick={() => navigator.clipboard.writeText(selected.url)}
          >
            Copy
          </button>
          <a
            href={selected.preview_url}
            download
            class="shrink-0 px-3 py-2 text-xs font-medium rounded-md border border-[#238636] bg-[#238636] hover:bg-[#2ea043] text-white transition-colors"
          >
            Download
          </a>
        </div>
        <div class="flex items-center gap-3 text-[10px] text-[#484f58]">
          <span>Uploaded {timeAgo(selected.uploaded)}</span>
          <span>by {selected.owner}</span>
          <span>Expires {new Date(selected.expires_at * 1000).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  </div>
{/if}
